const NO_DAM_DATA_SOURCES = Object.freeze({
  satellite: false,
  covermap: false,
  pollution: false,
  detection: false,
  livestock: false,
  cloudCheck: false,
  nationalPollutionSurvey: false,
  landCoverComparison: false,
  droneComparison: false,
});

const YEONGJU_DATA_SOURCES = Object.freeze({
  satellite: true,
  covermap: true,
  pollution: true,
  detection: true,
  livestock: true,
  cloudCheck: true,
  nationalPollutionSurvey: true,
  landCoverComparison: true,
  droneComparison: true,
});

// 2026-07-31: 용담은 YEONGJU_DATA_SOURCES 를 상속해 covermap/pollution/
// detection/livestock/cloudCheck 를 true 로 선언하고 있었으나, 실제로는
// 어느 것도 사용자에게 제공되지 않는 상태였다. 근거:
//   - GeoServer yongdam*/yongdamAOI* 워크스페이스에 AOI·하천·위성드론영상
//     외에 토지피복도/축산/우선관리지역 레이어가 아예 없음 (2026-07-31 실측).
//   - Yongdammap.js 가 wmsLayer4/5/6(토지피복도)·kareaLayer(우선관리지역)·
//     축산 6종을 전부 createEmptyVectorLayer() 로 만들고 있어, 스위치를 켜도
//     표시할 소스가 없음.
//   - Yongdammap.js 가 넘기는 droneTiff(yongjudata.js 의 DroneTiff)는 내용이
//     전부 주석 처리된 빈 배열.
// 플래그가 true 인 채로 두면 화면에는 "제공 중"으로 안내되면서 스위치는
// 아무 반응이 없어 고장으로 보인다. 실제 상태에 맞춰 전부 false 로 둔다.
// 용담에 데이터가 실제로 들어오면 해당 항목만 true 로 되돌리면 된다.

// enabled: 사업계약 전이라 화면은 아직 안 열지만 AOI 는 미리 띄워둔 2·3단계 확장 댐용.
// true 인 댐만 App.js 에서 실제 지도로 라우팅되고, false 면 안내 페이지(Error)로 라우팅된다.
//
// displayName/regionLabel/basinLabel: 지도 화면에 표시되는 "현재 댐" 컨텍스트
// UI 전용 값이다. weatherCity/geoServer workspace/dataArea 는 API·GeoServer
// 호출용 기술 키값이라 화면 표시에 재사용하지 않는다 (예: dataArea 'hoengseong'
// 이나 workspace 'Hoengseong_AOI' 를 사용자에게 그대로 보여주지 않기 위함).
// basinLabel 은 확인된 경우에만 채우고, 불확실하면 빈 문자열로 둔다.
const createStandardDam = (
  dataArea,
  weatherCity,
  workspace,
  extent,
  center,
  enabled = false,
  displayName,
  regionLabel,
  basinLabel = ''
) => ({
  dataArea,
  weatherCity,
  displayName,
  regionLabel,
  basinLabel,
  routeKind: 'stage',
  enabled,
  geoServer: {
    aoi: { workspace, layer: `${workspace}_AOI` },
    waterline: { workspace, layer: `${workspace}_Waterline` },
  },
  dataSources: NO_DAM_DATA_SOURCES,
  map: {
    extent,
    center,
    resetCenter: center,
    zoom: 11,
    minZoom: 9,
  },
});

const DAM_DATA = {
  Yeongju: {
    dataArea: 'yeongju',
    displayName: '영주댐',
    regionLabel: '경상북도 영주시',
    basinLabel: '내성천',
    weatherCity: 'Yeongju,KR',
    // Wikidata 인포박스 좌표(36°43'21.09"N 128°39'20.39"E). VWorld 기상청
    // "영주댐" 관측소 좌표(36.72629, 128.65502)와 420m 이내로 일치, 아래
    // damMarker 좌표(228p)와도 근접 — 교차검증됨. 'Yeongju,KR' 텍스트검색은
    // 우연히 200 이 오지만(2026-07-28 실측) 검증된 적 없어 좌표를 명시함.
    weatherCoordinates: { lat: 36.72253, lon: 128.65566 },
    dataSources: YEONGJU_DATA_SOURCES,
    geoServer: {
      aoi: { workspace: 'yeongjuAOI', layer: 'yeongju_63area_AOI' },
      waterline: { workspace: 'yeongju', layer: 'yeongju_waterline' },
      thematic: {
        riBoundary: { workspace: 'yeongjuAOI', layer: 'yeongjuAOI_ri_boundary' },
        subBasin4: { workspace: 'yeongjuAOI', layer: 'yeongjuAOI_4area' },
        subBasin11: { workspace: 'yeongjuAOI', layer: 'yeongjuAOI_11area' },
        coverLarge: { workspace: 'yeongju', layer: 'CoverMap_LargeCategory' },
        coverMiddle: { workspace: 'yeongju', layer: '2025_middle' },
        kwaterSmall: { workspace: 'yeongju', layer: '2025_kwater_small' },
        kwaterCover: { workspace: 'yeongju', layer: '202508_KwaterCoverMap' },
        coverSmall: { workspace: 'yeongju', layer: '2025_small' },
        coverLarge2024: {
          workspace: 'yeongju',
          layer: 'yeongjuLargeCategoryLayer_2024',
        },
      },
    },
    map: {
      center: [128.73239, 36.89602],
      resetCenter: [128.73239, 36.89602],
      zoom: 11,
      minZoom: 10,
      damMarker: {
        coordinate: [128.6552, 36.7234],
        name: 'yeongjudam Cluster',
        image: 'url/to/image.jpg',
        description: 'yeongjudam Cluster description',
        page: 'url/to/page',
      },
      droneMarkers: [
        {
          name: '0814_원천리109',
          jcode: 'j_290',
          image: '',
          coordinate: [128.768385583, 36.795768159],
          description: '',
        },
        {
          name: '0813_삼계리 129',
          jcode: 'j_289',
          image: '',
          coordinate: [128.733297456, 36.900191604],
          description: '',
        },
        {
          name: '0812_감곡리 931',
          jcode: 'j_288',
          image: '',
          coordinate: [128.641243149, 36.93315729],
          description: '',
        },
        {
          name: '0813_삼계리 129',
          jcode: 'j_311',
          image: '',
          coordinate: [128.733443124, 36.900441119],
          description: '',
        },
        {
          name: '0812_감곡리 931',
          jcode: 'j_314',
          image: '',
          coordinate: [128.64219097, 36.93269085],
          description: '',
        },
        {
          name: '11_원천리 109',
          jcode: 'j_378',
          image: '',
          coordinate: [128.768404912, 36.795891243],
          description: '',
        },
        {
          name: '11_감곡리 931',
          jcode: 'j_366',
          image: '',
          coordinate: [128.642037657, 36.932689081],
          description: '',
        },
        {
          name: '11_영주_호텔위즈',
          jcode: 'j_357',
          image: '',
          coordinate: [128.621903328, 36.815452506],
          description: '',
        },
      ],
    },
  },
  Daecheong: {
    dataArea: 'daecheong',
    displayName: '대청댐',
    regionLabel: '대전광역시 대덕구',
    basinLabel: '금강',
    weatherCity: 'Daejeon,KR',
    // 원래 있던 값(출처 미기록). 2026-07-28 재검증: Wikipedia 인포박스 좌표
    // (36°28'39"N 127°28'51"E = 36.47750, 127.48083)와 30m 이내로 일치해
    // 정확함을 확인, 값은 그대로 유지하고 출처만 보강함.
    weatherCoordinates: { lat: 36.4773, lon: 127.4809 },
    dataSources: NO_DAM_DATA_SOURCES,
    geoServer: {
      aoi: { workspace: 'Daecheong', layer: 'Daecheong_AOI' },
      waterline: { workspace: 'Daecheong', layer: 'Daecheong_waterline' },
    },
    map: {
      // GeoServer Daecheong_AOI extent center: [127.4017, 36.1601, 127.7135, 36.5557]
      center: [127.5576, 36.3579],
      resetCenter: [127.5576, 36.3579],
      zoom: 11,
      minZoom: 10,
    },
  },
  Bohyeonsan: {
    dataArea: 'bohyeonsan',
    displayName: '보현산댐',
    regionLabel: '경상북도 영천시',
    // 하천명(지류)을 정확히 확인하지 못해 빈 값으로 둔다.
    basinLabel: '',
    weatherCity: 'Yeongcheon,KR',
    // 원래 있던 값(출처 미기록). 2026-07-28 재검증: VWorld 로 찾은 신규
    // 후보 3곳(K-water 보현산댐지사/정확 지번 화북면 입석리 산42-1/
    // "보현산댐" 버스정류장)이 서로 1.5km 이내로 수렴하고, 이 기존 값과도
    // 3km 이내로 근접해 대체로 정확함을 확인. 참고: 이 좌표로 재조회하면
    // "Eisen" 이라는 낯선 지명이 나오는데, 신규 후보 3곳 전부 동일하게
    // "Eisen" 이 나와 좌표 오류가 아니라 이 지역 자체에 대한 OpenWeather
    // 의 고유(추정: 永川 의 옛 로마자 표기) 지명 등록으로 판단, 값 유지.
    weatherCoordinates: { lat: 36.1256, lon: 128.9127 },
    dataSources: NO_DAM_DATA_SOURCES,
    geoServer: {
      aoi: { workspace: 'Bohyeonsan', layer: 'Bohyeonsan_AOI' },
      waterline: { workspace: 'Bohyeonsan', layer: 'Bohyeonsan_Waterline' },
    },
    map: {
      // GeoServer Bohyeonsan:Bohyeonsan_AOI 의 EX_GeographicBoundingBox
      // 그대로(2026-07-31 GetCapabilities 실측). 이 필드가 없으면
      // resetDamMapView 가 고정 zoom(아래)만 쓰는데, 영주/대청과 똑같이
      // zoom 11 로 맞춰뒀던 게 보현산의 실제 유역(약 20.8x18.1km)에는 너무
      // 낮아(필요 줌 12.17) 화면의 절반도 못 채우고 있었다. extent 를
      // 채우면 다른 댐과 동일하게 fit() 이 화면 크기에 맞춰 알아서
      // 계산한다.
      extent: [128.832619, 36.021487, 129.064642, 36.184594],
      center: [128.94863, 36.10304],
      resetCenter: [128.9127, 36.1256],
      zoom: 11,
      minZoom: 10,
    },
  },
  Yongdam: {
    dataArea: 'yongdam',
    displayName: '용담댐',
    regionLabel: '전라북도 진안군',
    basinLabel: '금강',
    weatherCity: 'Jinan,KR',
    // Wikidata 인포박스 좌표(35°56'41"N 127°31'28"E). VWorld "용담댐물문화관"
    // 좌표(35.94401, 127.52879)와 근접 — 교차검증됨. Yongdammap.js 가 이미
    // 이 필드를 YeongjuMeuncontrol 에 전달하도록 연결돼 있어(2026-07-28) 값만
    // 채우면 즉시 적용된다.
    weatherCoordinates: { lat: 35.94472, lon: 127.52444 },
    dataSources: NO_DAM_DATA_SOURCES,
    // Yongdammap.js 는 이 map/geoServer 값을 아직 참조하지 않고 자체 상수
    // (AOI_EXTENT/AOI_CENTER)를 쓴다. ConfiguredDamMap 으로 통합할 때를
    // 대비해 미리 채워둔다 (값 출처: Yongdammap.js).
    geoServer: {
      aoi: { workspace: 'yongdamAOI', layer: 'yongdamCommonAOI' },
      waterline: { workspace: 'yongdamAOI', layer: 'yongdam_waterline' },
    },
    map: {
      extent: [127.34595801602278, 35.75281990450061, 127.58748536745351, 35.995244168923634],
      center: [127.4667, 35.874],
      resetCenter: [127.4667, 35.874],
      zoom: 11,
      minZoom: 9,
    },
  },
  Andong: {
    ...createStandardDam(
      'andong',
      'Andong,KR',
      'Andong',
      [128.72922609, 36.54298537, 129.19731894, 37.22178512],
      [128.96327252, 36.88238524],
      true,
      '안동댐',
      '경상북도 안동시',
      '낙동강'
    ),
    // Wikipedia 인포박스 좌표(36°35'05"N 128°46'26"E).
    weatherCoordinates: { lat: 36.58472, lon: 128.77389 },
  },
  Soyanggang: {
    ...createStandardDam(
      'soyanggang',
      'Chuncheon,KR',
      'Soyanggang',
      [127.79242882, 37.68802832, 128.58843499, 38.50005925],
      [128.19043191, 38.09404379],
      true,
      '소양강댐',
      '강원특별자치도 춘천시',
      '소양강'
    ),
    // Wikipedia 인포박스 좌표(37°56'44"N 127°48'52"E). VWorld 기상청
    // "소양강댐" 관측소 좌표(37.94248, 127.81713)와 340m 이내로 일치.
    weatherCoordinates: { lat: 37.94556, lon: 127.81444 },
  },
  Namgang: {
    ...createStandardDam(
      'namgang',
      'Jinju,KR',
      'Namgang',
      [127.48859248, 35.0820985, 128.21676387, 35.77179598],
      [127.85267818, 35.42694724],
      true,
      '남강댐',
      '경상남도 진주시',
      '남강'
    ),
    // VWorld "한국수자원공사남강댐지사" 좌표. Wikidata 진양호(저수지) 좌표
    // (35.17194, 128.03139)와 1.4km 이내로 근접해 채택. 참고: 기상청
    // "태수(남강댐)" 관측소(35.26871, 127.88902)는 댐에서 약 11km 떨어진
    // 이상치로 판단해 배제함 — 관측소 이름에 "(남강댐)"이 붙어있어도
    // 실제로는 다른 지점일 수 있음을 보여주는 사례.
    weatherCoordinates: { lat: 35.15923, lon: 128.02884 },
  },
  Juam: {
    ...createStandardDam(
      'juam',
      'Suncheon,KR',
      'Juam',
      [126.89450538, 34.67549953, 127.31779348, 35.24758242],
      [127.10614943, 34.96154097],
      true,
      '주암댐',
      '전라남도 순천시',
      '보성강'
    ),
    // VWorld 검색 결과 3곳(K-water 주암댐지사/주암댐관리체육공원/관리단입구)이
    // 300m 이내로 강하게 수렴 — 신뢰도 높음. 다만 이 좌표는 damData 의
    // extent(lon 최대 127.31779) 를 약 9.7km 벗어난다 — 댐 자체가 관측영역
    // 경계 부근에 위치한 것으로 보이며 오류로 판단하지 않음(회귀 테스트에서
    // 경고만 남김).
    weatherCoordinates: { lat: 34.94903, lon: 127.42503 },
  },
  Chungju: {
    ...createStandardDam(
      'chungju',
      'Chungju,KR',
      'Chungju',
      [127.921272, 36.796627, 129.018382, 37.820994],
      [128.469827, 37.308811],
      true,
      '충주댐',
      '충청북도 충주시',
      '남한강'
    ),
    // Wikipedia 인포박스 좌표(37°00'22"N 127°59'33"E). 주의: VWorld 검색으로
    // 나오는 기상청 "대화(충주댐)" 관측소(37.48709, 128.45401)는 실제로는
    // 이 지점에서 약 53km 떨어진 오류 매칭이었다 — 재조회 시 지명이
    // "Panghak-tong" 으로 나와 이상함을 감지, 위키백과 값으로 대체함.
    weatherCoordinates: { lat: 37.00611, lon: 127.9925 },
  },
  Hoengseong: {
    ...createStandardDam(
      'hoengseong',
      'Hoengseong,KR',
      'Hoengseong',
      [128.028321, 37.505786, 128.285512, 37.681118],
      [128.156917, 37.593452],
      true,
      '횡성댐',
      '강원특별자치도 횡성군',
      '섬강'
    ),
    // 'Hoengseong,KR' 는 OpenWeather 날씨 API 도시명 검색(q=)에서 404 남
    // (실측 확인, 2026-07-28). 기상청이 실제 운영하는 "횡성댐" 관측소 좌표
    // (VWorld POI 검색으로 확인, 위키백과 인포박스 좌표 37.54389/128.03167 와
    // 190m 이내로 일치 — 교차검증 완료). 관측영역(extent) 내부에 위치함.
    weatherCoordinates: { lat: 37.545588400629626, lon: 128.03292110009758 },
  },
  Seomjingang: {
    ...createStandardDam(
      'seomjingang',
      'Gokseong,KR',
      'Seomjingang',
      [126.859943, 35.394204, 127.478391, 35.835953],
      [127.169167, 35.615079],
      true,
      '섬진강댐',
      '전라북도 임실군',
      '섬진강'
    ),
    // Wikidata 인포박스 좌표(35°32'28"N 127°6'32"E). VWorld "한국수자원공사
    // 섬진강댐지사" 좌표(35.54269, 127.11123)와 180m 이내로 일치.
    weatherCoordinates: { lat: 35.54123, lon: 127.10904 },
  },
  Yeongcheon: {
    ...createStandardDam(
      'yeongcheon',
      'Yeongcheon,KR',
      'Yeongcheon',
      [128.972625, 36.055091, 129.204122, 36.240656],
      [129.088373, 36.147874],
      true,
      '영천댐',
      '경상북도 영천시',
      '금호강'
    ),
    // 'Yeongcheon,KR' 는 OpenWeather 날씨 API 도시명 검색(q=)에서 404 남
    // (실측 확인, 2026-07-28). 기상청이 실제 운영하는 "영천댐" 관측소 좌표
    // (VWorld POI 검색으로 확인). 디지털영천문화대전이 명시한 실제 소재지
    // "영천시 자양면 성곡리"와 일치, 관측영역(extent) 내부에 위치함.
    weatherCoordinates: { lat: 36.07768250095298, lon: 129.03197219976482 },
  },
  Hapcheon: {
    ...createStandardDam(
      'hapcheon',
      'Hapcheon,KR',
      'Hapcheon',
      [127.679293, 35.494525, 128.095114, 35.91415],
      [127.887203, 35.704338],
      true,
      '합천댐',
      '경상남도 합천군',
      '황강'
    ),
    // VWorld "한국수자원공사합천댐지사" 좌표. Global Energy Monitor 의
    // Hapcheon 수력발전소 좌표(35.53998, 128.08158)와 1.3km 이내로 근접.
    weatherCoordinates: { lat: 35.54607, lon: 128.06746 },
  },
  Miryang: {
    ...createStandardDam(
      'miryang',
      'Miryang,KR',
      'Miryang',
      [128.910348, 35.419436, 129.057098, 35.584773],
      [128.983723, 35.502105],
      true,
      '밀양댐',
      '경상남도 밀양시',
      '밀양강'
    ),
    // VWorld "밀양댐" 정확매칭 POI 좌표. 실제 소재지(밀양시 단장면 고례리)
    // 주소 지오코딩 결과(35.50656, 128.95533)와 3.6km 이내로 같은 권역 확인.
    weatherCoordinates: { lat: 35.4827, lon: 128.92736 },
  },
  Imha: {
    ...createStandardDam(
      'imha',
      'Andong,KR',
      'Imha',
      [128.861272, 36.22457, 129.294143, 36.840993],
      [129.077708, 36.532781],
      true,
      '임하댐',
      '경상북도 안동시',
      // 하천명(반변천으로 알려짐)을 이번 조사에서 직접 확인하지 못해 빈 값으로 둔다.
      ''
    ),
    // Wikipedia 인포박스 좌표(36°32'15"N 128°53'00"E). 주의: VWorld 검색으로
    // 나오는 기상청 "부남(임하댐)" 관측소(36.32413, 129.06649)는 실제로는
    // 이 지점에서 약 24km 떨어진 오류 매칭이었다 (Chungju 와 같은 패턴).
    weatherCoordinates: { lat: 36.5375, lon: 128.88333 },
  },
  Unmun: {
    ...createStandardDam(
      'unmun',
      'Cheongdo,KR',
      'Unmun',
      [128.907778, 35.607559, 129.109847, 35.84986],
      [129.008813, 35.72871],
      true,
      '운문댐',
      '경상북도 청도군',
      '동창천'
    ),
    // 댐 바로 하류 지번(청도군 운문면 방지리 197-1) 주소 지오코딩 좌표.
    // Wikidata "Unmun-myeon" 대표점(35.71537, 128.91289)과 190m 이내로 일치.
    // 'Cheongdo,KR' 텍스트 검색은 지금도 정상 작동하지만(2026-07-28 실측),
    // 전남 신안군의 동명 섬과 헷갈릴 위험이 있어(OpenWeather 지오코딩 API가
    // 실제로 그 섬으로 잘못 매칭한 사례 확인) 검증된 좌표로 명시함.
    weatherCoordinates: { lat: 35.71702, lon: 128.91812 },
  },
  Pyeonghwa: {
    ...createStandardDam(
      'pyeonghwa',
      'Hwacheon,KR',
      'Pyeonghwa',
      [127.394319, 38.199055, 127.888099, 38.594552],
      [127.641209, 38.396804],
      true,
      '평화의 댐',
      '강원특별자치도 화천군',
      // 하천명(북한강으로 알려짐)을 이번 조사에서 직접 확인하지 못해 빈 값으로 둔다.
      ''
    ),
    // Wikipedia 인포박스 좌표(38°12'29"N 127°50'43"E).
    weatherCoordinates: { lat: 38.208174, lon: 127.845411 },
  },
  Pyeonglim: {
    ...createStandardDam(
      'pyeonglim',
      'Jangseong,KR',
      'Pyeonglim',
      [126.609817, 35.135521, 126.736884, 35.351715],
      [126.67335, 35.243618],
      true,
      '평림댐',
      '전라남도 장성군',
      // 하천명을 확인하지 못해 빈 값으로 둔다.
      ''
    ),
    // 실제 소재지(장성군 삼계면 수옥리) 주소 지오코딩 좌표. VWorld 기상청
    // "평림댐" 관측소(35.28197, 126.68706)와 1.9km 이내로 근접해 교차검증됨.
    weatherCoordinates: { lat: 35.27778, lon: 126.70736 },
  },
  ChangnyeongHapcheon: {
    ...createStandardDam(
      'changnyeonghapcheon',
      'Changnyeong,KR',
      'ChangnyeongHapcheon',
      [128.276653, 35.586207, 128.46326, 35.735294],
      [128.369957, 35.660751],
      true,
      // ⚠️ 2026-07-28 정정: '창녕함안보'(창녕군 길곡면–함안군 칠북면, 낙동강
      // 하류의 별개 보)로 잘못 조사해 넣었던 값. 사용자 지적으로 확인해보니
      // 이 damKey(ChangnyeongHapcheon)는 '합천창녕보'(창녕군 이방면–합천군
      // 청덕면)를 가리킨다 — 이 저장소의 src/pages/MainPage.js 에 이미
      // "name: '창녕합천보', coordinate: [128.3626, 35.5912], adress: '경남
      // 창녕군 이방면 장천리 1032-1'" 로 기록돼 있었지만, 정부·K-water 공식
      // 명칭은 '합천창녕보'다. 창녕함안보와 합천창녕보는 낙동강의 별개 시설이다.
      '합천창녕보',
      '경상남도 창녕군·합천군',
      '낙동강'
    ),
    // 보의 좌안(창녕군 이방면 등림리 565-1)·우안(합천군 청덕면 삼학리
    // 221-2) 정확 지번의 중간점. 이 저장소 MainPage.js 의 기존 좌표
    // [128.3626, 35.5912] 와 350m 이내, 관리사무소 추정 주소(창녕군 이방면
    // 장천리) 지오코딩 결과와도 350m 이내로 — 3개 독립 값이 모두 수렴함.
    weatherCoordinates: { lat: 35.59167, lon: 128.35747 },
  },
};

// 메타데이터(displayName 등) 없이 새 댐이 추가되는 경우를 대비한 안전 표시값.
// dataArea('hoengseong')나 workspace 같은 기술 키값이 화면에 그대로 노출되는
// 사고를 막기 위한 최후 방어선이다.
const FALLBACK_DISPLAY_META = Object.freeze({
  displayName: '선택된 댐',
  regionLabel: '',
  basinLabel: '',
});

export const getDamData = (damKey) => {
  const dam = DAM_DATA[damKey] || {
    dataArea: damKey.toLowerCase(),
    weatherCity: 'Seoul,KR',
    dataSources: NO_DAM_DATA_SOURCES,
    enabled: false,
  };
  return dam.displayName ? dam : { ...FALLBACK_DISPLAY_META, ...dam };
};

// createStandardDam 으로 만든 2·3단계 확장 댐 키 목록 (App.js 라우트 자동 생성용).
// App.js 순서(Andong→…→ChangnyeongHapcheon)와 동일하게 유지해 diff 를 최소화한다.
export const STAGE_DAM_KEYS = Object.keys(DAM_DATA).filter(
  (damKey) => DAM_DATA[damKey].routeKind === 'stage'
);
